"use client";

import { Info, Check, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ForensicScores } from "@/lib/forensics/load-forensics";
import type { AltmanZone } from "@/lib/forensics/scores";

const ZONE_LABEL: Record<AltmanZone, string> = {
  sicher: "Sichere Zone",
  grauzone: "Grauzone",
  distress: "Distress-Zone",
};

const ZONE_COLOR: Record<AltmanZone, string> = {
  sicher: "text-positive",
  grauzone: "text-gold-text",
  distress: "text-negative",
};

function InfoTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={`Erklärung: ${label}`} className="text-muted-foreground hover:text-ink">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{children}</TooltipContent>
    </Tooltip>
  );
}

/** 9 Segmente statt reiner Zahl - macht den Piotroski-Score auf einen Blick als Anteil
 * erfassbar (Feedback-Runde 4, Punkt 9: "visuell aufhübschen"). */
function SegmentMeter({ score, max }: { score: number; max: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }, (_, i) => (
        <div key={i} className={cn("h-2 flex-1 rounded-full", i < score ? "bg-positive" : "bg-hairline")} />
      ))}
    </div>
  );
}

/** Zonen-Gauge fuer Altman/Beneish (Feedback-Runde 4, Punkt 9) - zeigt die drei Zonen als
 * feste Balken-Abschnitte (Breite proportional zum jeweiligen Wertebereich innerhalb
 * [min,max]) plus einen Marker an der tatsaechlichen Position. Die Grenzwerte spiegeln
 * dieselben Konstanten wie `lib/forensics/scores.ts` (ALTMAN_SAFE_THRESHOLD/
 * ALTMAN_DISTRESS_THRESHOLD/BENEISH_THRESHOLD, dort nicht exportiert) - rein visuell,
 * die tatsaechliche Zonen-Einordnung kommt weiterhin von `scores.altman.zone`/
 * `scores.beneish.aboveThreshold` vom Server. */
function ZoneGauge({
  value,
  min,
  max,
  zones,
}: {
  value: number;
  min: number;
  max: number;
  /** Aufsteigend sortierte Zonengrenzen mit Farbe des Abschnitts LINKS der Grenze. */
  zones: { upTo: number; colorClass: string }[];
}) {
  const clamped = Math.max(min, Math.min(max, value));
  const markerPct = ((clamped - min) / (max - min)) * 100;
  return (
    <div className="relative h-2 w-full">
      <div className="flex h-2 w-full overflow-hidden rounded-full">
        {zones.map((zone, i) => {
          const prevBound = i === 0 ? min : zones[i - 1].upTo;
          const width = ((zone.upTo - prevBound) / (max - min)) * 100;
          return <div key={i} className={zone.colorClass} style={{ width: `${width}%` }} />;
        })}
      </div>
      <div
        className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
        style={{ left: `${markerPct}%` }}
      />
    </div>
  );
}

/**
 * Forensik-Trio (backlog.md -> D5, von Erik als Praktiker-Glaubwürdigkeits-Priorität
 * markiert): Piotroski F-Score, Altman Z-Score, Beneish M-Score - drei etablierte,
 * akademische Bilanzanalyse-Kennzahlen, neutral/deskriptiv erklärt (keine Kauf-/
 * Verkaufssprache, insbesondere beim Beneish-Score bewusst "auffällig laut Modell"
 * statt "Manipulation nachgewiesen" formuliert).
 */
export function ForensicScoresPanel({ scores }: { scores: ForensicScores }) {
  // Spaltenzahl an tatsaechlich vorhandene Scores anpassen (Feedback-Runde 4, Punkt 9) -
  // `sm:grid-cols-3` liess bei fehlendem Beneish-Score (haeufig, siehe Docstring unten)
  // eine sichtbare leere dritte Spalte auf breiten Viewports zurueck.
  const presentCount = [scores.piotroski, scores.altman, scores.beneish].filter(Boolean).length;
  const gridColsClass = presentCount >= 3 ? "sm:grid-cols-3" : presentCount === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";
  return (
    <div className="flex flex-col gap-3">
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        Zur Datengrundlage
        <InfoTooltip label="Datengrundlage Forensik-Trio">
          Alle drei Scores basieren auf rohen SEC-Bilanzpositionen (u. a. Bilanzsumme,
          Forderungen, Sachanlagen, Periodenabgrenzungen). Anders als die meisten anderen
          Kennzahlen auf Erikonomie - etwa KGV oder ROE in den Tabs oben - werden diese
          Rohwerte nicht gegen eine zweite, unabhängige Datenquelle abgeglichen, da es dafür
          aktuell keine kostenlose Zweitquelle gibt. Die Extraktion aus den SEC-Einreichungen
          selbst erfolgt sorgfältig (u. a. Mehrfach-Tag-Fallback, Aktiensplit-Schutz) - nur
          eben aus einer einzigen Quelle.
        </InfoTooltip>
      </span>
      <div className={cn("grid grid-cols-1 gap-6", gridColsClass)}>
        {scores.piotroski && (
          <div className="flex flex-col gap-2 rounded-lg border border-hairline p-4">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
              Piotroski F-Score
              <InfoTooltip label="Piotroski F-Score">
                Zählt 9 Kriterien zu Profitabilität, Verschuldung/Liquidität und operativer
                Effizienz, die sich gegenüber dem Vorjahr jeweils verbessert haben - je 1
                Punkt pro erfülltem Kriterium. Klassischer akademischer Indikator für die
                fundamentale Qualitätsentwicklung eines Unternehmens (Piotroski, 2000).
              </InfoTooltip>
            </span>
            <span className="font-tabular text-3xl font-semibold text-ink">
              {scores.piotroski.score}
              <span className="text-base font-normal text-muted-foreground"> / 9</span>
            </span>
            <SegmentMeter score={scores.piotroski.score} max={9} />
            <ul className="mt-1 flex flex-col gap-1 text-xs text-ink-secondary">
              {scores.piotroski.criteria.map((c) => (
                <li key={c.key} className="flex items-start gap-1.5">
                  {c.passed ? (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-positive" />
                  ) : (
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {scores.altman && (
          <div className="flex flex-col gap-2 rounded-lg border border-hairline p-4">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
              Altman Z-Score
              <InfoTooltip label="Altman Z-Score">
                Kombiniert Working Capital, Bilanzgewinn, Ergebnis vor Steuern (Näherung für
                EBIT), Marktkapitalisierung und Umsatz - jeweils im Verhältnis zur
                Bilanzsumme bzw. zu den Verbindlichkeiten - zu einer statistischen
                Kennzahl für Insolvenzrisiko (Altman, 1968). {">"} 2,99 = sichere Zone,
                1,81-2,99 = Grauzone, {"<"} 1,81 = Distress-Zone.
              </InfoTooltip>
            </span>
            <span className="font-tabular text-3xl font-semibold text-ink">{scores.altman.zScore.toFixed(2)}</span>
            <ZoneGauge
              value={scores.altman.zScore}
              min={0}
              max={4.5}
              zones={[
                { upTo: 1.81, colorClass: "bg-negative" },
                { upTo: 2.99, colorClass: "bg-gold" },
                { upTo: 4.5, colorClass: "bg-positive" },
              ]}
            />
            <span className={cn("text-sm font-medium", ZONE_COLOR[scores.altman.zone])}>
              {ZONE_LABEL[scores.altman.zone]}
            </span>
          </div>
        )}

        {scores.beneish && (
          <div className="flex flex-col gap-2 rounded-lg border border-hairline p-4">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
              Beneish M-Score
              <InfoTooltip label="Beneish M-Score">
                Vergleicht acht Jahr-über-Jahr-Kennzahlen (u. a. Forderungsentwicklung,
                Bruttomarge, Bilanzstruktur, Abschreibungsrate, Periodenabgrenzungen) zu
                einer statistischen Kennzahl, die in akademischen Studien mit
                Gewinnmanipulation korreliert (Beneish, 1999). Eine Modellaussage über
                Auffälligkeit, kein Nachweis für das konkrete Unternehmen.
              </InfoTooltip>
            </span>
            <span className="font-tabular text-3xl font-semibold text-ink">{scores.beneish.mScore.toFixed(2)}</span>
            <ZoneGauge
              value={scores.beneish.mScore}
              min={-4}
              max={0}
              zones={[
                { upTo: -1.78, colorClass: "bg-positive" },
                { upTo: 0, colorClass: "bg-negative" },
              ]}
            />
            <span
              className={cn("text-sm font-medium", scores.beneish.aboveThreshold ? "text-negative" : "text-positive")}
            >
              {scores.beneish.aboveThreshold ? "Auffälliger Bereich laut Modell" : "Unauffälliger Bereich laut Modell"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
